import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { CopyableCode } from '../../components/ui/CopyableCode';
import { Markdown } from '../../components/ui/Markdown';
import { TableTools } from '../../components/ui/TableTools';
import { num } from '../../format';
import { useAuth } from '../auth/AuthContext';
import { ComponentPicker } from '../components/ComponentPicker';
import { listSubstitutes, type ComponentBrief } from '../components/api';
import { useNavigation } from '../layout/navigation';
import { BomImport } from './BomImport';
import { BomComparison } from './BomComparison';
import { ProductionForm } from './ProductionForm';
import {
  addAlternative,
  addLine,
  archiveSpec,
  restoreSpec,
  createVersion,
  deleteAlternative,
  deleteLine,
  deleteVersion,
  deprecateVersion,
  downloadBom,
  getSpec,
  getVersion,
  releaseVersion,
  updateLine,
  updateSpec,
  type SpecLine,
  type SpecVersion,
} from './api';

interface Props {
  specId: string;
  onArchived: () => void;
}

/**
 * One specification: its versions down the top, the chosen version's lines below.
 *
 * The screen is built around the one rule that cannot be softened — a released
 * version is read-only. Rather than showing controls that then refuse, the
 * editing affordances simply are not there, and the way forward from a released
 * version is the button that clones it into a new draft.
 */
export function SpecPanel({ specId, onArchived }: Props) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [versionId, setVersionId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [outputComponent, setOutputComponent] = useState<ComponentBrief | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canDraft = can('spec.draft.write');
  const canRelease = can('spec.release');

  const spec = useQuery({ queryKey: ['specs', specId], queryFn: () => getSpec(specId) });

  // Open the draft if there is one — that is the version somebody came here to
  // work on. Otherwise the newest, which is what is being built from.
  useEffect(() => {
    if (!spec.data || versionId) return;
    const versions = spec.data.versions;
    const draft = versions.find((row) => row.status === 'draft');
    const newest = versions[versions.length - 1];
    setVersionId(draft?.id ?? newest?.id ?? null);
  }, [spec.data, versionId]);

  const version = useQuery({
    queryKey: ['spec-versions', versionId],
    queryFn: () => getVersion(versionId as string),
    enabled: Boolean(versionId),
  });

  const fail = (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown');

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['specs'] });
    void queryClient.invalidateQueries({ queryKey: ['spec-versions'] });
  }

  const save = useMutation({
    mutationFn: (input: { name: string; code: string; description: string; source: string; outputComponentId: string | null }) =>
      updateSpec(specId, {
        name: input.name,
        code: input.code || null,
        description: input.description || null,
        source_url: input.source || null,
        output_component_id: input.outputComponentId,
      }),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const archive = useMutation({
    mutationFn: () => archiveSpec(specId),
    onSuccess: () => {
      refresh();
      onArchived();
    },
    onError: fail,
  });

  const restore = useMutation({
    mutationFn: () => restoreSpec(specId),
    onSuccess: () => refresh(),
    onError: fail,
  });

  const clone = useMutation({
    mutationFn: (fromId: string | null) => createVersion(specId, fromId),
    onSuccess: (created) => {
      setVersionId(created.id);
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const release = useMutation({
    mutationFn: (id: string) => releaseVersion(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const deprecate = useMutation({
    mutationFn: (id: string) => deprecateVersion(id),
    onSuccess: () => refresh(),
    onError: fail,
  });

  const discard = useMutation({
    mutationFn: (id: string) => deleteVersion(id),
    onSuccess: () => {
      setVersionId(null);
      refresh();
    },
    onError: fail,
  });

  if (spec.isLoading || !spec.data) return <p className="panel__hint">{t('common.loading')}</p>;

  const item = spec.data;
  const current = version.data ?? null;
  const isDraft = current?.status === 'draft';

  async function download(format: 'csv' | 'xlsx') {
    if (!current) return;
    const blob = await downloadBom(current.id, format);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${current.code}-v${current.version}.${format}`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <article className="panel">
      {editing ? (
        <form
          className="panel__form"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            save.mutate({
              name: String(form.get('name')),
              code: String(form.get('code') ?? ''),
              description: String(form.get('description') ?? ''),
              source: String(form.get('source_url') ?? ''),
              outputComponentId: outputComponent?.id ?? null,
            });
          }}
        >
          <label>
            {t('specs.name')}
            <input name="name" defaultValue={item.name} required />
          </label>
          <label>
            {t('specs.code')}
            <input name="code" defaultValue={item.code ?? ''} />
          </label>
          <label>
            {t('specs.sourceUrl')}
            <input
              name="source_url"
              type="url"
              defaultValue={item.source_url ?? ''}
              placeholder="https://"
            />
          </label>
          <ComponentPicker
            label={t('specs.output')}
            value={outputComponent}
            onChange={setOutputComponent}
          />
          <label>
            {t('specs.description')}
            <textarea name="description" defaultValue={item.description ?? ''} rows={6} />
            <span className="component-form__parsed">{t('common.markdownHint')}</span>
          </label>
          <div className="panel__actions">
            <button type="submit" disabled={save.isPending}>
              {t('common.save')}
            </button>
            <button type="button" onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      ) : (
        <header className="panel__head">
          <div>
            <h2>{item.name}</h2>
            <p className="panel__sub">
              {item.archived_at && (
                <span className="badge badge--warn">{t('common.archived')}</span>
              )}
              {item.code && <CopyableCode value={item.code} label={t('specs.code')} />}
              <span>{t('specs.versionCount', { n: item.versions.length })}</span>
            </p>
            {item.description && (
              <div className="panel__description">
                <Markdown source={item.description} lines={3} />
              </div>
            )}
            {item.source_url && (
              <p className="spec__source">
                <a href={item.source_url} target="_blank" rel="noreferrer">
                  {t('specs.source')}
                </a>
              </p>
            )}
          </div>
          {canDraft && item.archived_at && (
            <div className="panel__actions">
              {/* Archiving destroyed nothing, so the way back is one press. */}
              <button type="button" onClick={() => restore.mutate()} disabled={restore.isPending}>
                {t('common.restore')}
              </button>
            </div>
          )}
          {canDraft && !item.archived_at && (
            <div className="panel__actions">
              <button
                type="button"
                onClick={() => {
                  setOutputComponent(item.output_component);
                  setEditing(true);
                }}
              >
                {t('common.edit')}
              </button>
              <button type="button" className="danger" onClick={() => archive.mutate()}>
                {t('specs.archive')}
              </button>
            </div>
          )}
        </header>
      )}

      {error && (
        <p className="panel__error">{t(`specs.errors.${error}`, t('specs.errors.unknown'))}</p>
      )}

      <nav className="spec__versions">
        {item.versions.map((row) => (
          <button
            key={row.id}
            type="button"
            className={`spec__version spec__version--${row.status}${
              row.id === versionId ? ' is-selected' : ''
            }`}
            onClick={() => setVersionId(row.id)}
          >
            {t('specs.versionShort', { version: row.version })}
            <em>{t(`specs.status.${row.status}`)}</em>
          </button>
        ))}
        {canDraft && !item.versions.some((row) => row.status === 'draft') && (
          <button
            type="button"
            className="spec__version spec__version--new"
            onClick={() => clone.mutate(current?.id ?? null)}
            title={t('specs.cloneHint')}
          >
            + {t('specs.newVersion')}
          </button>
        )}
      </nav>

      {current && (
        <>
          <div className="spec__version-head">
            <CopyableCode value={current.code} className="components__code" label={t('specs.versionCode')} />
            <span className={`spec__badge spec__badge--${current.status}`}>
              {t(`specs.status.${current.status}`)}
            </span>
            {current.released_at && (
              <span className="stock__when">
                {t('specs.releasedAt', {
                  when: new Date(current.released_at).toLocaleDateString(),
                })}
              </span>
            )}
            {/* Short, because it is compared by eye against another version's:
                identical hashes mean the two versions say the same thing. */}
            {current.content_hash && (
              <span className="spec__hash" title={current.content_hash}>
                {current.content_hash.slice(0, 8)}
              </span>
            )}

            <span className="spec__version-actions">
              {/* The export is also the template: its columns are the ones the
                  import reads back, so a version can be taken out, edited in
                  Excel and loaded into a draft without anything in between. */}
              <TableTools
                label={t('specs.exportAsTemplate')}
                onExport={() => void download('xlsx')}
              />
              <button type="button" onClick={() => void download('csv')}>
                {t('specs.exportCsv')}
              </button>
              {isDraft && canDraft && (
                <button type="button" onClick={() => setImporting((open) => !open)}>
                  {t('specs.import.button')}
                </button>
              )}
              {isDraft && canRelease && (
                <button
                  type="button"
                  disabled={release.isPending || current.lines.length === 0}
                  onClick={() => release.mutate(current.id)}
                >
                  {t('specs.release')}
                </button>
              )}
              {isDraft && canDraft && (
                <button
                  type="button"
                  className="danger"
                  onClick={() => discard.mutate(current.id)}
                >
                  {t('specs.discardDraft')}
                </button>
              )}
              {current.status === 'released' && canDraft && (
                <button type="button" onClick={() => clone.mutate(current.id)}>
                  {t('specs.reviseFrom', { version: current.version })}
                </button>
              )}
              {current.status === 'released' && canRelease && (
                <button type="button" onClick={() => deprecate.mutate(current.id)}>
                  {t('specs.deprecate')}
                </button>
              )}
            </span>
          </div>

          {!isDraft && <p className="panel__hint">{t('specs.frozenHint')}</p>}
          {can('stock.post') && <ProductionForm version={current} output={item.output_component} />}

          {importing && isDraft && (
            <BomImport
              versionId={current.id}
              onImported={() => {
                setImporting(false);
                refresh();
              }}
              onClose={() => setImporting(false)}
            />
          )}

          <LineTable version={current} editable={Boolean(isDraft && canDraft)} onChanged={refresh} />

          <BomComparison spec={item} />

          {isDraft && canDraft && <AddLine versionId={current.id} onAdded={refresh} />}
        </>
      )}
    </article>
  );
}

function LineTable({
  version,
  editable,
  onChanged,
}: {
  version: SpecVersion;
  editable: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: (lineId: string) => deleteLine(version.id, lineId),
    onSuccess: onChanged,
  });

  if (version.lines.length === 0) {
    return <p className="panel__hint">{t('specs.noLines')}</p>;
  }

  return (
    <table className="spec__lines">
      <thead>
        <tr>
          <th>#</th>
          <th>{t('specs.designators')}</th>
          {/* Quantity before the part: it is one short number against a cell
              that carries the name, the alternatives and the note, and a column
              of numbers is only readable when it starts at the same place on
              every row. */}
          <th className="spec__qty">{t('specs.quantity')}</th>
          <th>{t('specs.part')}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {version.lines.map((line) =>
          editing === line.id ? (
            <EditLine
              key={line.id}
              version={version}
              line={line}
              onDone={() => {
                setEditing(null);
                onChanged();
              }}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <tr key={line.id} className={line.is_optional ? 'spec__line--optional' : undefined}>
              <td>{line.line_no}</td>
              <td className="spec__designators">{line.reference_designators ?? ''}</td>
              <td className="spec__qty">
                {num(line.quantity)}
                {line.is_optional && <em> {t('specs.optionalShort')}</em>}
              </td>
              <td>
                <LineTarget line={line} />
                {line.alternatives.length > 0 && (
                  <ul className="spec__alternatives">
                    {line.alternatives.map((alt) => (
                      <li key={alt.id}>
                        {t('specs.orPart')} <ComponentLink component={alt.component} />
                        {alt.note && <em> — {alt.note}</em>}
                        {editable && (
                          <button
                            type="button"
                            className="link"
                            onClick={() =>
                              void deleteAlternative(version.id, line.id, alt.id).then(onChanged)
                            }
                          >
                            ×
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {editable && (
                  <AddAlternative
                    versionId={version.id}
                    lineId={line.id}
                    componentId={line.component?.id}
                    onAdded={onChanged}
                  />
                )}
                {line.note && <p className="spec__note">{line.note}</p>}
              </td>
              <td className="spec__row-actions">
                {editable && (
                  <>
                    {/* Editing rather than delete-and-retype: a line carries
                        designators, a note and its alternatives, and all of
                        those are lost by the round trip through deletion. */}
                    <button type="button" onClick={() => setEditing(line.id)}>
                      {t('common.edit')}
                    </button>
                    <button
                      type="button"
                      className="danger"
                      onClick={() => remove.mutate(line.id)}
                    >
                      {t('common.delete')}
                    </button>
                  </>
                )}
              </td>
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
}

/**
 * One line, open for editing.
 *
 * The part itself can be swapped as well as the numbers: "wrong resistor, right
 * position" is the commonest correction there is, and deleting the line to
 * retype it would take the designators and the alternatives with it.
 *
 * A sub-assembly line keeps its assembly — repointing a BOM at a different
 * version is not a typo fix, and doing it by accident from a text field is not
 * a thing that should be possible.
 */
function EditLine({
  version,
  line,
  onDone,
  onCancel,
}: {
  version: SpecVersion;
  line: SpecLine;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [part, setPart] = useState<ComponentBrief | null>(line.component);
  const [designators, setDesignators] = useState(line.reference_designators ?? '');
  const [quantity, setQuantity] = useState(num(line.quantity));
  const [optional, setOptional] = useState(line.is_optional);
  const [note, setNote] = useState(line.note ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      updateLine(version.id, line.id, {
        // Only for part lines: an assembly keeps the version it was pinned to.
        ...(line.child_version_id ? {} : { component_id: part?.id ?? line.component_id }),
        quantity,
        reference_designators: designators || null,
        is_optional: optional,
        note: note || null,
      }),
    onSuccess: onDone,
    onError: (exc) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  return (
    <tr className="spec__line--editing">
      <td>{line.line_no}</td>
      <td>
        <input
          value={designators}
          onChange={(event) => setDesignators(event.target.value)}
          placeholder={t('specs.designators')}
          size={10}
        />
      </td>
      {/* Same order as the display row above, or editing a line makes the
          table jump. */}
      <td className="spec__qty">
        <input
          value={quantity}
          onChange={(event) => setQuantity(event.target.value.replace(',', '.'))}
          inputMode="decimal"
          size={5}
          aria-label={t('specs.quantity')}
        />
        <label className="spec__optional">
          <input
            type="checkbox"
            checked={optional}
            onChange={(event) => setOptional(event.target.checked)}
          />
          {t('specs.optional')}
        </label>
      </td>
      <td>
        {line.child_version ? (
          <LineTarget line={line} />
        ) : (
          <ComponentPicker label={t('specs.part')} value={part} onChange={setPart} />
        )}
        <input
          className="spec__note-input"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={t('specs.lineNote')}
        />
        {error && (
          <span className="panel__error">
            {t(`specs.errors.${error}`, t('specs.errors.unknown'))}
          </span>
        )}
      </td>
      <td className="spec__row-actions">
        <button
          type="button"
          disabled={save.isPending || !quantity || Number(quantity) <= 0}
          onClick={() => save.mutate()}
        >
          {t('common.save')}
        </button>
        <button type="button" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </td>
    </tr>
  );
}

/** A line is a part or a sub-assembly, and the two must not look alike: one is
 *  bought, the other is built. */
function LineTarget({ line }: { line: SpecLine }) {
  const { t } = useTranslation();

  if (line.child_version) {
    return (
      <span className="spec__assembly">
        {t('specs.assembly')} <strong>{line.child_version.code}</strong>{' '}
        <em>{t('specs.versionShort', { version: line.child_version.version })}</em>
      </span>
    );
  }
  return (
    <span>
      <ComponentLink component={line.component} />{' '}
      <em>{line.component?.mpn ?? line.component?.internal_code ?? ''}</em>
    </span>
  );
}

/** A component can occur as either the chosen part or one of its approved
 * substitutes. Both names lead to the same catalogue record. */
function ComponentLink({ component }: { component: ComponentBrief | null | undefined }) {
  const { t } = useTranslation();
  const navigation = useNavigation();

  if (!component) return null;
  if (!navigation.canOpen('component')) return <strong>{component.name}</strong>;

  return (
    <button
      type="button"
      className="link"
      title={t('purchases.openComponent')}
      onClick={() => navigation.open('component', component.id)}
    >
      {component.name}
    </button>
  );
}

function AddAlternative({
  versionId,
  lineId,
  componentId,
  onAdded,
}: {
  versionId: string;
  lineId: string;
  /** The part this line calls for, if it calls for a part at all — a
   *  sub-assembly line has none. Its catalogue substitutes are offered below. */
  componentId?: string | null;
  onAdded: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [part, setPart] = useState<ComponentBrief | null>(null);
  const [note, setNote] = useState('');

  // Offered, never applied. A catalogue substitute is a property of the part —
  // "these two usually do each other's job"; an alternative on a line is an
  // engineer's decision about this circuit. Turning the first into the second
  // automatically would put a part into a BOM that nobody chose.
  const suggestions = useQuery({
    queryKey: ['substitutes', componentId],
    queryFn: () => listSubstitutes(componentId as string),
    enabled: open && Boolean(componentId),
  });

  const add = useMutation({
    mutationFn: () =>
      addAlternative(versionId, lineId, {
        component_id: (part as ComponentBrief).id,
        note: note || null,
      }),
    onSuccess: () => {
      setPart(null);
      setNote('');
      setOpen(false);
      onAdded();
    },
  });

  if (!open) {
    return (
      <button type="button" className="link" onClick={() => setOpen(true)}>
        + {t('specs.addAlternative')}
      </button>
    );
  }

  const offered = (suggestions.data ?? []).filter(
    (row) => row.component.id !== part?.id,
  );

  return (
    <span className="spec__alt-form">
      <ComponentPicker
        label={t('specs.part')}
        value={part}
        onChange={setPart}
        exclude={componentId ?? undefined}
      />

      {offered.length > 0 && (
        <span className="spec__alt-suggested">
          <em>{t('specs.suggestedFromCatalogue')}</em>
          {offered.map((row) => (
            <button
              key={row.id}
              type="button"
              className="link"
              title={row.note ?? undefined}
              onClick={() => setPart(row.component)}
            >
              {row.component.name}
            </button>
          ))}
        </span>
      )}

      <input
        value={note}
        placeholder={t('specs.alternativeNote')}
        onChange={(event) => setNote(event.target.value)}
      />
      <button type="button" disabled={!part || add.isPending} onClick={() => add.mutate()}>
        {t('common.add')}
      </button>
      <button type="button" onClick={() => setOpen(false)}>
        {t('common.cancel')}
      </button>
    </span>
  );
}

function AddLine({ versionId, onAdded }: { versionId: string; onAdded: () => void }) {
  const { t } = useTranslation();
  const [part, setPart] = useState<ComponentBrief | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [designators, setDesignators] = useState('');
  const [optional, setOptional] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      addLine(versionId, {
        component_id: (part as ComponentBrief).id,
        quantity,
        reference_designators: designators || null,
        is_optional: optional,
      }),
    onSuccess: () => {
      setPart(null);
      setQuantity('1');
      setDesignators('');
      setOptional(false);
      setError(null);
      onAdded();
    },
    onError: (exc) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  return (
    <form
      className="spec__add-line"
      onSubmit={(event) => {
        event.preventDefault();
        if (part) add.mutate();
      }}
    >
      <p className="spec__add-title">{t('specs.addLine')}</p>
      <input
        value={designators}
        placeholder={t('specs.designators')}
        onChange={(event) => setDesignators(event.target.value)}
        size={12}
      />
      <ComponentPicker label={t('specs.part')} value={part} onChange={setPart} />
      <input
        value={quantity}
        onChange={(event) => setQuantity(event.target.value.replace(',', '.'))}
        inputMode="decimal"
        size={5}
        aria-label={t('specs.quantity')}
      />
      <label className="spec__optional">
        <input
          type="checkbox"
          checked={optional}
          onChange={(event) => setOptional(event.target.checked)}
        />
        {t('specs.optional')}
      </label>
      <button type="submit" disabled={!part || add.isPending}>
        {t('common.add')}
      </button>
      {error && (
        <span className="panel__error">
          {t(`specs.errors.${error}`, t('specs.errors.unknown'))}
        </span>
      )}
    </form>
  );
}
